import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Globe2, BarChart3, Calculator, DollarSign, Hash, Map as MapIcon, Share2,
  TrendingDown, Flag,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * Bottom section rail for the /international surfaces — the same pattern as
 * the scenario page's ScenarioSectionRail (Master 2026-07-12: "a new map
 * footer extension with overview and other slots just like the scenarios
 * tab").
 *
 * On the HUB (/international) the first slots are in-page SECTIONS with
 * scroll-spy (Overview → top, Savings → #savings, Countries → #countries);
 * the remaining slots navigate to the tool pages. On every other
 * /international/* page all slots are page links, with Overview leading back
 * to the hub.
 */

interface RailItem {
  key: string;
  label: string;
  icon: LucideIcon;
  /** In-page section id (hub only). */
  sectionId?: string;
  /** Route target when not a hub section (or when off the hub). */
  to?: string;
  match?: (p: string) => boolean;
}

const ITEMS: RailItem[] = [
  { key: "overview", label: "Overview", icon: Globe2, sectionId: "intl-overview", to: "/international", match: (p) => p === "/international" },
  { key: "savings", label: "Savings", icon: TrendingDown, sectionId: "intl-savings", to: "/international" },
  { key: "countries", label: "Countries", icon: Flag, sectionId: "intl-countries", to: "/international" },
  { key: "compare", label: "Compare", icon: BarChart3, to: "/international/detailed", match: (p) => p.startsWith("/international/detailed") },
  { key: "calculator", label: "Calculator", icon: Calculator, to: "/international/calculator", match: (p) => p.startsWith("/international/calculator") },
  { key: "challenge", label: "$100", icon: DollarSign, to: "/international/100-dollar-challenge", match: (p) => p.startsWith("/international/100") },
  { key: "codes", label: "Codes", icon: Hash, to: "/international/code-explorer", match: (p) => p.startsWith("/international/code-explorer") },
  { key: "heatmap", label: "Heatmap", icon: MapIcon, to: "/international/heatmap", match: (p) => p.startsWith("/international/heatmap") },
  { key: "share", label: "Share", icon: Share2, to: "/international/share", match: (p) => p.startsWith("/international/share") },
];

export default function InternationalSectionRail() {
  const brand = useBrand();
  const isAhp = brand.key === "ahp";
  const { isDark } = useTheme();
  const dark = isAhp && isDark;
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const railRef = useRef<HTMLDivElement>(null);

  const onHub = pathname === "/international";
  const [activeSection, setActiveSection] = useState("overview");

  // Scroll-spy on the hub: the active section is the last one whose top has
  // scrolled above a trigger line just below the fixed header. Mirrors
  // ScenarioSectionRail's compute loop.
  useEffect(() => {
    if (!onHub) return;
    let ticking = false;
    const compute = () => {
      ticking = false;
      const headerH = (document.querySelector("header") as HTMLElement)?.offsetHeight ?? 70;
      const threshold = headerH + 56;
      let current = "overview";
      for (const it of ITEMS) {
        if (!it.sectionId) continue;
        const el = document.getElementById(it.sectionId);
        if (el && el.getBoundingClientRect().top <= threshold) current = it.key;
      }
      setActiveSection(current);
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(compute); } };
    window.addEventListener("scroll", onScroll, { passive: true });
    compute();
    return () => window.removeEventListener("scroll", onScroll);
  }, [onHub]);

  const activeKey = onHub
    ? activeSection
    : (ITEMS.find((it) => it.match?.(pathname))?.key ?? "overview");

  // Keep the active chip visible in the rail.
  useEffect(() => {
    const el = railRef.current?.querySelector<HTMLElement>(`[data-rail-key="${activeKey}"]`);
    el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [activeKey]);

  const goSection = useCallback((it: RailItem) => {
    if (it.key === "overview") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = it.sectionId ? document.getElementById(it.sectionId) : null;
    if (!el) return;
    const headerH = (document.querySelector("header") as HTMLElement)?.offsetHeight ?? 70;
    const y = el.getBoundingClientRect().top + window.scrollY - headerH - 12;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  }, []);

  if (!isAhp || !isMobile || !pathname.startsWith("/international")) return null;

  const barBg = dark ? "rgba(13,30,53,0.94)" : "rgba(255,255,255,0.94)";
  const border = dark ? "rgba(197,165,90,0.22)" : "rgba(27,27,27,0.10)";
  const idle = dark ? "rgba(240,235,227,0.62)" : "rgba(27,27,27,0.62)";
  const accent = dark ? "#C5A55A" : "#B22234";
  const activeBg = dark ? "rgba(197,165,90,0.16)" : "rgba(178,34,52,0.08)";

  const chipStyle = (on: boolean): React.CSSProperties => ({
    color: on ? accent : idle,
    backgroundColor: on ? activeBg : "transparent",
    border: `1px solid ${on ? `${accent}55` : "transparent"}`,
    fontFamily: "'Instrument Sans', system-ui, sans-serif",
    fontSize: 12,
    fontWeight: on ? 700 : 500,
    whiteSpace: "nowrap",
    textDecoration: "none",
  });

  return (
    <div
      ref={railRef}
      className="fixed left-0 right-0 flex items-stretch gap-1.5 px-2.5 overflow-x-auto no-scrollbar"
      style={{
        // Ride directly above the NativeTabBar (content ~59px + its trimmed
        // safe-area inset; keep this in sync with NativeTabBar paddingBottom).
        bottom: "calc(59px + max(env(safe-area-inset-bottom, 0px) - 26px, 6px))",
        zIndex: 9990,
        height: 46,
        backgroundColor: barBg,
        borderTop: `1px solid ${border}`,
        backdropFilter: "saturate(180%) blur(16px)",
        WebkitBackdropFilter: "saturate(180%) blur(16px)",
        scrollbarWidth: "none",
      }}
    >
      {ITEMS.map((it) => {
        const Icon = it.icon;
        const on = it.key === activeKey;
        const isHubSection = onHub && !!it.sectionId;
        if (isHubSection) {
          return (
            <button
              key={it.key}
              type="button"
              data-rail-key={it.key}
              onClick={() => goSection(it)}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 my-1.5 rounded-full transition-colors"
              style={chipStyle(on)}
            >
              <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={on ? 2.4 : 2} />
              {it.label}
            </button>
          );
        }
        return (
          <Link
            key={it.key}
            to={it.to || "/international"}
            data-rail-key={it.key}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 my-1.5 rounded-full transition-colors"
            style={chipStyle(on)}
          >
            <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={on ? 2.4 : 2} />
            {it.label}
          </Link>
        );
      })}
    </div>
  );
}
